import { SlideHeader } from "../../../SlideHeader";

const STEPS: Array<{
  when: string;
  name: string;
  detail: string;
  now?: boolean;
}> = [
  {
    when: "Today",
    name: "Constant ASO",
    detail:
      "The foundation: listings that convert, audited daily. This is the wedge — and the data source everything to the right feeds on.",
    now: true,
  },
  {
    when: "Next",
    name: "UA creative briefs",
    detail:
      "The audit already knows the winning keywords, competitor gaps, and value-prop framing. Generate the ad brief from them — same data, new artifact, sold to customers we already have.",
  },
  {
    when: "Then",
    name: "MMP integration",
    detail:
      "Ingest attribution from AppsFlyer / Adjust so every shipped revision traces to installs and revenue: “revision X lifted attributed installs Y%.” The foothold to become the ASO-aware measurement layer.",
  },
  {
    when: "Later",
    name: "AI ad creative",
    detail:
      "Extend the asset-generation engine from store creatives to paid-acquisition creatives — the same rendering pipeline pointed at a bigger canvas.",
  },
];

export default function ExpansionPath() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix E · Expansion Path">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          From ASO into the{" "}
          <span className="text-blue-700">full growth loop.</span>
        </h1>
        <p className="mt-3 text-lg font-medium italic tracking-tight text-slate-700 md:text-xl">
          The audit signals that fix the listing are the same signals that
          price the next three products.
        </p>
      </SlideHeader>

      {/* The path — four steps, one data spine */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:gap-4">
        {STEPS.map((s, i) => (
          <div
            key={s.name}
            className={`flex flex-col rounded-xl p-4 ring-1 md:p-5 ${
              s.now ? "bg-blue-600 ring-blue-600" : "bg-white ring-slate-200"
            }`}
          >
            <p
              className={`text-xs font-semibold uppercase tracking-[0.15em] ${
                s.now ? "text-blue-100" : "text-blue-700"
              }`}
            >
              {i + 1} · {s.when}
            </p>
            <p
              className={`mt-1.5 text-lg font-bold tracking-tight md:text-xl ${
                s.now ? "text-white" : "text-slate-900"
              }`}
            >
              {s.name}
            </p>
            <p
              className={`mt-2 text-xs leading-snug md:text-sm ${
                s.now ? "text-blue-50" : "text-slate-600"
              }`}
            >
              {s.detail}
            </p>
          </div>
        ))}
      </div>

      {/* Industry terms — context, not co-equal strategy */}
      <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3 ring-1 ring-slate-200 md:mt-5 md:px-5">
        <p className="text-xs leading-relaxed text-slate-600 md:text-sm">
          <span className="font-semibold text-slate-900">Terms:</span>{" "}
          <span className="font-semibold">MMP</span> — mobile measurement
          partner: the attribution platforms (AppsFlyer, Adjust, Singular)
          every paid-UA publisher runs to know which campaign drove which
          install. <span className="font-semibold">UA tooling</span> — the
          software layer on top of the $78B/yr paid-acquisition spend: bid
          optimization, creative testing, channel management.
        </p>
      </div>

      {/* The arc */}
      <div className="mt-4 rounded-xl bg-blue-50 px-5 py-3.5 ring-1 ring-blue-200 md:mt-auto md:px-6 md:py-4">
        <p className="text-sm font-semibold leading-snug text-blue-900 md:text-base">
          From “we make your listing convert” to owning the loop: listing →
          ad → install → revenue.
        </p>
      </div>
    </div>
  );
}
